import { useAuth } from "../auth/AuthProvider";
import { useAsync } from "../hooks/useAsync";
import { RatioCell, StateBlock, money, count } from "../components/StateBlock";
import type { ChannelPerformanceResponse } from "../api/types";

export function AdPerformance({ brandId }: { brandId: string }) {
  const { api } = useAuth();
  const enabled = brandId.trim().length > 0;
  const { data, loading, error, reload } = useAsync<ChannelPerformanceResponse>(
    (signal) => api.getChannelPerformance(brandId.trim(), signal),
    [brandId],
    enabled,
  );

  if (!enabled) {
    return (
      <div className="state state--empty" role="status">
        Enter a brand ID above to load ad performance.
      </div>
    );
  }

  const channels = data?.channels ?? [];
  const currency = data?.currency ?? "USD";

  return (
    <section aria-labelledby="ap-heading">
      <h2 id="ap-heading">Ad Performance</h2>
      <p className="section-note">
        Per-channel spend, delivery, and efficiency (ROAS, ROI, CPA).
      </p>
      <StateBlock
        loading={loading}
        error={error}
        isEmpty={channels.length === 0}
        emptyMessage="No ad performance data available for this brand yet."
        onRetry={reload}
      >
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Ad performance per channel</caption>
            <thead>
              <tr>
                <th scope="col">Channel</th>
                <th scope="col" className="num">
                  Spend
                </th>
                <th scope="col" className="num">
                  Impressions
                </th>
                <th scope="col" className="num">
                  Clicks
                </th>
                <th scope="col" className="num">
                  Engagements
                </th>
                <th scope="col" className="num">
                  Conversions
                </th>
                <th scope="col" className="num">
                  ROAS
                </th>
                <th scope="col" className="num">
                  ROI
                </th>
                <th scope="col" className="num">
                  CPA
                </th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.channel}>
                  <th scope="row" className="channel">
                    {c.channel}
                  </th>
                  <td className="num">{money(c.spend, currency)}</td>
                  <td className="num">{count(c.impressions)}</td>
                  <td className="num">{count(c.clicks)}</td>
                  <td className="num">{count(c.engagements)}</td>
                  <td className="num">
                    {(c.adConversions ?? 0).toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="num">
                    <RatioCell ratio={c.roas} suffix="×" />
                  </td>
                  <td className="num">
                    <RatioCell ratio={c.roi} suffix="×" />
                  </td>
                  <td className="num">
                    <RatioCell ratio={c.cpa} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StateBlock>
    </section>
  );
}
